import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@common/musicHubCommon';
import { useUserStore } from '../../../store';
import { ROUTES } from '../../../routes';
import { reportPageView } from '../../../services/reportLog';
import './index.scss';

// 默认状态 - 暂无接口数据，后续接入后替换
const DEFAULT_STATS = {
  plays: '0',
  likes: '0',
  comments: '0',
  followers: '0',
};

// 歌曲数据类型 - 接口接入后使用
interface SongStatsItem {
  id: string;
  title: string;
  album: string;
  cover?: string;
  plays: number;
  likes: number;
  comments: number;
  tags: string[];
  releaseDate: string;
}

// 歌曲数据 - 预留，接口接入后替换
const MOCK_SONGS: SongStatsItem[] = [];

// 统计卡片组件
interface StatCardProps {
  label: string;
  value: string;
}

const StatCard: React.FC<StatCardProps> = ({ label, value }) => (
  <div className="stat-card">
    <span className="stat-card__label">{label}</span>
    <span className="stat-card__value">{value}</span>
  </div>
);

// 歌曲性能卡片组件
interface SongStatsCardProps {
  song: SongStatsItem;
}

const SongStatsCard: React.FC<SongStatsCardProps> = ({ song }) => (
  <div className="song-stats-card">
    <div className="song-stats-card__header">
      <div className="song-stats-card__cover">
        {song.cover ? (
          <img src={song.cover} alt={song.title} />
        ) : (
          <div className="song-stats-card__cover-placeholder">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>
            </svg>
          </div>
        )}
      </div>
      <div className="song-stats-card__info">
        <h3 className="song-stats-card__title">{song.title}</h3>
        <p className="song-stats-card__album">{song.album}</p>
        <div className="song-stats-card__tags">
          {song.tags.map(tag => (
            <span key={tag} className="song-stats-card__tag">{tag}</span>
          ))}
        </div>
        <p className="song-stats-card__release">Release: {song.releaseDate}</p>
      </div>
    </div>
    <div className="song-stats-card__stats">
      <div className="song-stats-card__stat">
        <span className="song-stats-card__stat-label">Plays</span>
        <span className="song-stats-card__stat-value">{song.plays.toLocaleString()}</span>
      </div>
      <div className="song-stats-card__stat-divider" />
      <div className="song-stats-card__stat">
        <span className="song-stats-card__stat-label">Likes</span>
        <span className="song-stats-card__stat-value">{song.likes.toLocaleString()}</span>
      </div>
      <div className="song-stats-card__stat-divider" />
      <div className="song-stats-card__stat">
        <span className="song-stats-card__stat-label">Comments</span>
        <span className="song-stats-card__stat-value">{song.comments}</span>
      </div>
    </div>
  </div>
);

// 分段控制组件
interface SegmentedControlProps {
  options: { label: string; value: string }[];
  value: string;
  onChange: (value: string) => void;
}

const SegmentedControl: React.FC<SegmentedControlProps> = ({ options, value, onChange }) => (
  <div className="segmented-control">
    {options.map(option => (
      <button
        key={option.value}
        className={`segmented-control__btn ${value === option.value ? 'segmented-control__btn--active' : ''}`}
        onClick={() => onChange(option.value)}
      >
        {option.label}
      </button>
    ))}
  </div>
);

// 上传图标
const UploadIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M9 16h6v-6h4l-7-7-7 7h4v6zm-4 2h14v2H5v-2z"/>
  </svg>
);

const Dashboard: React.FC = () => {
  const user = useUserStore((state) => state.user);
  const navigate = useNavigate();
  const [timeRange, setTimeRange] = useState('all');

  useEffect(() => {
    reportPageView('content_0007', { withArtistId: true });
  }, []);

  return (
    <div className="dashboard">
      {/* Hero 区域 - 带艺人封面 */}
      <div className="dashboard__hero">
        <div className="hero-overlay" />
        <div className="hero-content">
          <h1 className="hero-content__name">{user?.name || 'Artist'}</h1>
        </div>
      </div>

      {/* 移动端 Release Song 按钮 */}
      <div className="dashboard__mobile-release">
        <Button 
          type="primary"
          className="release-btn"
          onClick={() => navigate(ROUTES.releaseType)}
        >
          <UploadIcon />
          <span>Release Song</span>
        </Button>
      </div>

      {/* 总体统计 */}
      <section className="dashboard__section">
        <h2 className="section-title">Total Stats</h2>
        <div className="stats-grid">
          <StatCard label="Plays" value={DEFAULT_STATS.plays} />
          <div className="stats-divider" />
          <StatCard label="Likes" value={DEFAULT_STATS.likes} />
          <div className="stats-divider" />
          <StatCard label="Comments" value={DEFAULT_STATS.comments} />
          <div className="stats-divider" />
          <StatCard label="Followers" value={DEFAULT_STATS.followers} />
        </div>
      </section>

      {/* 歌曲性能 */}
      <section className="dashboard__section">
        <div className="section-header">
          <h2 className="section-title">Song Performance</h2>
          <SegmentedControl
            options={[
              { label: '7 Days', value: '7d' },
              { label: '1 Month', value: '30d' },
              { label: 'All', value: 'all' },
            ]}
            value={timeRange}
            onChange={setTimeRange}
          />
        </div>
        <div className="songs-grid">
          {MOCK_SONGS.length > 0 ? (
            MOCK_SONGS.map(song => (
              <SongStatsCard key={song.id} song={song} />
            ))
          ) : (
            <div className="songs-empty">
              <div className="songs-empty__icon">
                <img src="https://akoss.bangcdn.net/fe/MusicBig.svg" alt="" width={64} height={64} />
              </div>
              <p className="songs-empty__text">No Songs</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Dashboard;
